import React, { useEffect, useState } from "react";
import api from "../lib/api";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Checkbox } from "../components/ui/checkbox";
import { Badge } from "../components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "../components/ui/dialog";
import { FileText, Printer, Trash2, Check } from "lucide-react";
import { DOC_TYPES, DOC_CATEGORIES, getDocType } from "../lib/docTypes";
import { getSchema } from "../lib/docSchemas";
import { getDefaultContent } from "../lib/docTemplates";
import { printDocOnly, printInstructionRequest } from "../lib/printDoc";
import SignaturePad from "../components/SignaturePad";
import { toast } from "sonner";

export default function Documents() {
  const [items, setItems] = useState([]);
  const [patients, setPatients] = useState([]);
  const [org, setOrg] = useState({});
  const [filterCat, setFilterCat] = useState("");
  const [newOpen, setNewOpen] = useState(false);
  const [form, setForm] = useState({ doc_type: "", patient_id: "", title: "", content: {} });
  const [viewing, setViewing] = useState(null);

  const load = () => api.get("/documents").then((r) => setItems(r.data));
  useEffect(() => {
    load();
    api.get("/patients").then((r) => setPatients(r.data));
    api.get("/organization").then((r) => setOrg(r.data));
  }, []);

  const patientName = (pid) => patients.find((p) => p.id === pid)?.name_kanji || "-";
  const patient = (pid) => patients.find((p) => p.id === pid);

  const onSelectType = (doc_type) => {
    const dt = getDocType(doc_type);
    setForm({ doc_type, patient_id: "", title: dt?.name || "", content: { ...getDefaultContent(doc_type) } });
    setNewOpen(true);
  };

  const save = async () => {
    if (!form.doc_type || !form.title) { toast.error("書類タイプ・タイトルは必須です"); return; }
    const r = await api.post("/documents", form);
    toast.success("書類を作成しました");
    setNewOpen(false);
    load();
    setViewing(r.data);
  };

  const update = async (doc, patch) => {
    const r = await api.patch(`/documents/${doc.id}`, patch);
    setViewing(r.data);
    load();
  };

  const del = async (id) => {
    if (!confirm("削除しますか?")) return;
    await api.delete(`/documents/${id}`);
    if (viewing?.id === id) setViewing(null);
    load();
  };

  const filteredTypes = filterCat ? DOC_TYPES.filter((d) => d.category === filterCat) : DOC_TYPES;

  return (
    <div className="space-y-6" data-testid="documents-page">
      <div>
        <div className="text-xs tracking-[0.2em] uppercase text-[#8A8A86] font-numeric mb-2">DOCUMENTS</div>
        <h1 className="text-3xl sm:text-4xl font-bold tracking-tight">書類管理</h1>
        <p className="text-[#4A4A48] mt-1">全 {DOC_TYPES.length} 種類の書類テンプレート・事業所情報・利用者情報を自動差し込み</p>
      </div>

      <div className="flex flex-wrap gap-2">
        <button
          onClick={() => setFilterCat("")}
          className={`px-3 py-1.5 text-sm rounded-full border transition ${
            !filterCat ? "bg-[#2C5545] text-white border-[#2C5545]" : "bg-white border-[#E5E5E3]"
          }`}
          data-testid="cat-all"
        >
          全て
        </button>
        {DOC_CATEGORIES.map((c) => (
          <button
            key={c}
            onClick={() => setFilterCat(c)}
            className={`px-3 py-1.5 text-sm rounded-full border transition ${
              filterCat === c ? "bg-[#2C5545] text-white border-[#2C5545]" : "bg-white border-[#E5E5E3]"
            }`}
            data-testid={`cat-${c}`}
          >
            {c}
          </button>
        ))}
      </div>

      <Card className="p-6 border-[#E5E5E3]">
        <h3 className="text-lg font-bold mb-4">テンプレートから作成</h3>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
          {filteredTypes.map((d) => (
            <button
              key={d.id}
              data-testid={`template-${d.id}`}
              onClick={() => onSelectType(d.id)}
              className="p-4 text-left border border-[#E5E5E3] rounded-lg hover:bg-[#F3F3F1] hover:border-[#2C5545] transition"
            >
              <FileText size={18} className="text-[#2C5545] mb-2" />
              <div className="text-sm font-medium">{d.name}</div>
              <div className="text-xs text-[#8A8A86] mt-1">{d.category}</div>
            </button>
          ))}
        </div>
      </Card>

      <Card className="border-[#E5E5E3] bg-white">
        <div className="p-6 border-b border-[#E5E5E3]">
          <h3 className="text-lg font-bold">作成済み書類 ({items.length}件)</h3>
        </div>
        <div className="divide-y divide-[#E5E5E3]">
          {items.length === 0 && <div className="py-8 text-center text-[#8A8A86]">書類がありません</div>}
          {items.map((d) => (
            <div key={d.id} className="p-4 flex items-center gap-4">
              <FileText size={18} className="text-[#2C5545]" />
              <div className="flex-1 min-w-0">
                <div className="font-medium truncate">{d.title}</div>
                <div className="text-xs text-[#8A8A86] font-numeric">
                  {getDocType(d.doc_type)?.category} · {d.patient_id ? patientName(d.patient_id) : "事業所書類"} · {d.created_at?.slice(0, 10)}
                </div>
              </div>
              <Badge variant="outline">{d.status === "finalized" ? "確定" : "下書き"}</Badge>
              <button data-testid={`view-doc-${d.id}`} onClick={() => setViewing(d)} className="text-[#2C5545] hover:underline text-sm">開く</button>
              <button onClick={() => del(d.id)} className="text-[#A74141]"><Trash2 size={14} /></button>
            </div>
          ))}
        </div>
      </Card>

      <Dialog open={newOpen} onOpenChange={setNewOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>{getDocType(form.doc_type)?.name} 作成</DialogTitle></DialogHeader>
          <div className="space-y-3 py-3">
            <div>
              <Label>対象利用者 (任意)</Label>
              <select value={form.patient_id} onChange={(e) => setForm({ ...form, patient_id: e.target.value })} className="flex h-10 w-full rounded-md border border-input px-3 text-sm">
                <option value="">事業所書類</option>
                {patients.map((p) => <option key={p.id} value={p.id}>{p.name_kanji}</option>)}
              </select>
            </div>
            <div><Label>タイトル</Label><Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setNewOpen(false)}>キャンセル</Button>
            <Button data-testid="create-doc-button" className="bg-[#2C5545] text-white" onClick={save}>作成して編集</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {viewing && (
        <DocViewer
          doc={viewing}
          patient={patient(viewing.patient_id)}
          org={org}
          onClose={() => setViewing(null)}
          onUpdate={(patch) => update(viewing, patch)}
        />
      )}
    </div>
  );
}

function DocViewer({ doc, patient, org, onClose, onUpdate }) {
  return <DocViewerImpl doc={doc} patient={patient} org={org} onClose={onClose} onUpdate={onUpdate} />;
}

const cbMark = (v) => (v ? "☑" : "□");

// 訪問看護指示書依頼書 専用プレビュー(主治医宛の依頼状レイアウト・原本に忠実)
function InstructionRequestLetter({ content: c, org }) {
  const o = org || {};
  const postal = o.postal_code ? `〒${o.postal_code}　` : "";
  const bodyText = (c.greeting_body || "").replace(/\s*敬白\s*$/, "").trim();
  const lbl = { display: "inline-block", width: "6em", verticalAlign: "top" };
  return (
    <div data-testid="instruction-request-letter" style={{ fontFamily: '"Yu Mincho","MS Mincho","Hiragino Mincho ProN",serif', color: "#111", fontSize: "13px", lineHeight: 2 }}>
      {/* 最上部: 区分(左) */}
      <div>{cbMark(c.request_type === "新規")} 新規　　　　{cbMark(c.request_type === "継続")} 継続</div>

      {/* 右: 依頼年月日 / 受理担当 */}
      <div style={{ textAlign: "right", marginBottom: "1.4em" }}>
        <div>依頼年月日　　{c.request_date || "　　　年　　月　　日"}</div>
        <div>依頼受理担当者様　{c.receiver_handler || "＿＿＿＿＿＿＿"}</div>
      </div>

      {/* 依頼区分 (インデント・枠なし) */}
      <div style={{ paddingLeft: "2.5em", marginBottom: "1.2em" }}>
        <div>訪問看護指示書依頼書</div>
        <div>{cbMark(!!c.req_self_support_cert)} 自立支援医療受給者証（精神通院医療）診断書依頼書</div>
        <div style={{ paddingLeft: "1.5em" }}>{cbMark(c.self_support_type === "新規")} 新規　　{cbMark(c.self_support_type === "更新")} 更新</div>
        <div>{cbMark(!!c.req_self_support_add)} 自立支援医療機関追加用意見書依頼書（訪問看護）</div>
      </div>

      {/* 宛先 */}
      <div><span style={lbl}>医療機関名</span>{c.medical_institution || ""}　{c.department || ""}</div>
      <div style={{ marginBottom: "1em" }}><span style={lbl}>主治医名</span>{c.doctor_name || ""}　{c.doctor_honorific || "先生御侍史"}</div>

      {/* 本文 */}
      <div style={{ whiteSpace: "pre-wrap", marginBottom: "0.3em" }}>{bodyText}</div>
      <div style={{ textAlign: "right", marginBottom: "0.3em" }}>敬白</div>

      <div style={{ textAlign: "center", marginBottom: "0.8em" }}>記</div>

      {/* 対象者 (インデント・枠なし) */}
      <div style={{ paddingLeft: "2.5em", marginBottom: "1.2em" }}>
        <div><span style={lbl}>フリガナ</span>{c.patient_kana || ""}</div>
        <div><span style={lbl}>患者氏名</span>{c.patient_name || ""}　　様</div>
        <div><span style={lbl}>生年月日</span>{c.patient_birth || ""}</div>
        <div><span style={lbl}>性別</span>{c.patient_gender || ""}</div>
        <div><span style={lbl}>住所</span>{c.patient_address || ""}</div>
        <div><span style={lbl}>電話番号</span>{c.patient_phone || ""}</div>
      </div>

      {/* 依頼内容 (インデント・枠なし) */}
      <div style={{ paddingLeft: "2.5em" }}>
        <div>訪問看護指示書　利用目的：　{c.purpose || ""}</div>
        <div style={{ paddingLeft: "1em" }}>・訪問看護指示開始日　{c.instruction_start_date || ""}</div>
        <div style={{ paddingLeft: "1em" }}>・訪問内容　{c.visit_content || ""}</div>
        <div>{cbMark(!!c.req_iv_instruction)} 在宅患者訪問点滴注射指示書</div>
        <div>{cbMark(!!c.req_special_instruction)} 特別訪問看護指示書</div>
        <div>{cbMark(!!c.req_self_support_cert)} 自立支援医療受給者証（精神通院医療）診断書依頼書</div>
        <div style={{ paddingLeft: "1.5em" }}>{cbMark(c.self_support_type === "新規")} 新規　　{cbMark(c.self_support_type === "更新")} 更新　期限{c.self_support_limit || "　　　　"}（診断書 {cbMark(c.self_support_cert_needed === "要")} 要 {cbMark(c.self_support_cert_needed === "不要")} 不）</div>
        <div>{cbMark(!!c.req_self_support_add)} 自立支援医療機関追加用意見書　訪問看護ステーション</div>
      </div>
      <div style={{ textAlign: "right", marginBottom: "2.2em" }}>以上</div>

      {/* 差出人 (右寄せ・枠なし) */}
      <div style={{ textAlign: "right", lineHeight: 1.95 }}>
        <div>{o.name || ""}</div>
        <div>{postal}{o.address || ""}</div>
        <div>電話 {o.phone || ""}{o.fax ? `　FAX ${o.fax}` : ""}</div>
        {(o.manager_name || o.emergency_phone) && (
          <div>事務担当　{o.manager_name || ""}{o.emergency_phone ? `　携帯${o.emergency_phone}` : ""}</div>
        )}
        {o.seal_base64 && <img src={o.seal_base64} alt="印" style={{ height: "3.5em", marginTop: "0.3em" }} />}
      </div>
    </div>
  );
}

export function DocViewerImpl({ doc, patient, org, onClose, onUpdate, onCreate }) {
  const [content, setContent] = useState(doc.content || {});
  const [title, setTitle] = useState(doc.title);
  const [referrers, setReferrers] = useState([]);
  const schema = getSchema(doc.doc_type);
  const isFinalized = doc.status === "finalized";
  const isNew = !!doc._new || !doc.id;

  useEffect(() => {
    api.get("/referrers").then((r) => setReferrers(r.data)).catch(() => {});
  }, []);

  const save = () => onUpdate({ title, content });
  const finalize = () => onUpdate({ title, content, status: "finalized" });
  const createDraft = () => onCreate?.({ title, content });
  const revert = () => {
    if (!window.confirm("この書類の確定を解除して編集可能にしますか?(管理者権限が必要)")) return;
    onUpdate({ status: "draft" });
  };
  const setField = (k, v) => {
    if (isFinalized) return;
    setContent({ ...content, [k]: v });
  };

  // 医療機関マスタから自動入力
  const applyReferrer = (refId) => {
    const r = referrers.find((x) => x.id === refId);
    if (!r) return;
    const next = { ...content };
    // 共通: 主治医情報
    next.doctor_name = r.person_in_charge || next.doctor_name || "";
    next.doctor_phone = r.phone || next.doctor_phone || "";
    // 申込書系
    next.referrer = r.name || next.referrer || "";
    next.referrer_contact = r.phone || next.referrer_contact || "";
    // 報告書系
    next.addressee = r.name || next.addressee || "";
    next.report_to = r.person_in_charge ? `${r.name} ${r.person_in_charge}` : (r.name || next.report_to || "");
    // 退院時共同指導
    next.hospital = r.name || next.hospital || "";
    next.hospital_doctor = r.person_in_charge || next.hospital_doctor || "";
    // 多職種連携記録
    next.partner_name = r.person_in_charge || next.partner_name || "";
    next.partner_org = r.name || next.partner_org || "";
    next.partner_role = r.kind || next.partner_role || "";
    setContent(next);
    toast.success(`${r.name} の情報を自動入力しました`);
  };

  // Pre-fill from patient/org if content is empty
  const prefillFromMaster = () => {
    const next = { ...content };
    if (patient) {
      if (!next.doctor_name) next.doctor_name = patient.primary_doctor_name || "";
      if (!next.doctor_phone) next.doctor_phone = patient.doctor_phone || "";
      if (!next.client_name) next.client_name = patient.name_kanji || "";
      // 指示書依頼書 用 利用者差し込み
      if (!next.patient_kana) next.patient_kana = patient.name_kana || "";
      if (!next.patient_name) next.patient_name = patient.name_kanji || "";
      if (!next.patient_birth) next.patient_birth = patient.birth_date || "";
      if (!next.patient_gender) next.patient_gender = patient.gender || "";
      if (!next.patient_address) next.patient_address = patient.address || "";
      if (!next.patient_phone) next.patient_phone = patient.phone || "";
      if (!next.medical_institution) next.medical_institution = patient.primary_medical_institution || "";
      const ecs = patient.emergency_contacts || [];
      if (ecs[0] && !next.family_contact1) next.family_contact1 = `${ecs[0].name || ""} (${ecs[0].relation || ""}) ${ecs[0].phone || ""}`;
      if (ecs[1] && !next.family_contact2) next.family_contact2 = `${ecs[1].name || ""} (${ecs[1].relation || ""}) ${ecs[1].phone || ""}`;
      if (ecs[0] && !next.contact_1) next.contact_1 = `${ecs[0].name || ""} (${ecs[0].relation || ""}) ${ecs[0].phone || ""}`;
      if (ecs[1] && !next.contact_2) next.contact_2 = `${ecs[1].name || ""} (${ecs[1].relation || ""}) ${ecs[1].phone || ""}`;
    }
    if (org) {
      if (!next.emergency_contact) next.emergency_contact = org.emergency_phone || "";
      if (!next.station_contact) next.station_contact = org.emergency_phone || org.phone || "";
      if (!next.emergency_hours) next.emergency_hours = org.business_hours || "";
      if (!next.complaint_window) next.complaint_window = org.complaint_contact || "";
    }
    if (!next.plan_date && !next.issue_date && !next.contract_date) {
      next.date = new Date().toISOString().slice(0, 10);
    }
    setContent(next);
    toast.success("利用者・事業所情報から自動入力しました");
  };

  const renderField = (f) => {
    const v = content[f.key] ?? "";
    if (f.type === "textarea") {
      return <Textarea rows={f.rows || 3} value={v} disabled={isFinalized} onChange={(e) => setField(f.key, e.target.value)} />;
    }
    if (f.type === "select") {
      return (
        <select value={v} disabled={isFinalized} onChange={(e) => setField(f.key, e.target.value)} className="flex h-10 w-full rounded-md border border-input px-3 text-sm bg-white disabled:bg-[#F3F3F1] disabled:cursor-not-allowed">
          <option value="">選択してください</option>
          {f.options.map((o) => <option key={o} value={o}>{o}</option>)}
        </select>
      );
    }
    if (f.type === "check") {
      return (
        <label className={`flex items-center gap-2 py-1 ${isFinalized ? "cursor-not-allowed opacity-70" : "cursor-pointer"}`}>
          <Checkbox checked={!!v} disabled={isFinalized} onCheckedChange={(val) => setField(f.key, val)} />
          <span className="text-sm">{f.label}</span>
        </label>
      );
    }
    if (f.type === "signature") {
      return (
        <SignaturePad
          value={v}
          onChange={(b64) => setField(f.key, b64)}
          label={f.label}
          testid={`signature-${f.key}`}
          disabled={isFinalized}
        />
      );
    }
    return <Input type={f.type || "text"} value={v} disabled={isFinalized} onChange={(e) => setField(f.key, e.target.value)} />;
  };

  return (
    <Dialog open={true} onOpenChange={(o) => { if (!o) onClose?.(); }}>
      <DialogContent
        data-testid="doc-viewer-dialog"
        className="max-w-6xl w-[95vw] max-h-[95vh] p-0 overflow-hidden gap-0"
      >
        <DialogTitle className="sr-only">{title || "書類"}</DialogTitle>
        <div className="flex flex-col max-h-[95vh]">
          <div className="no-print flex items-center justify-between p-4 border-b border-[#E5E5E3] bg-white shrink-0 mr-12">
            <div className="flex-1 min-w-0 mr-4">
              <Input value={title} disabled={isFinalized} onChange={(e) => setTitle(e.target.value)} className="text-lg font-bold border-none shadow-none px-0 focus-visible:ring-0 disabled:opacity-100 disabled:cursor-not-allowed" data-testid="doc-title-input" />
              <div className="text-xs font-numeric flex items-center gap-2">
                <span className="text-[#8A8A86]">{getDocType(doc.doc_type)?.category}</span>
                {isNew ? (
                  <span data-testid="doc-new-badge" className="inline-flex items-center gap-1 text-[#8B6424] bg-[#FFF8E6] border border-[#D4A373]/40 rounded-full px-2 py-0.5 font-medium">
                    新規(未保存)
                  </span>
                ) : isFinalized ? (
                  <span data-testid="doc-finalized-badge" className="inline-flex items-center gap-1 text-[#2C5545] bg-[#D9E2D5]/50 border border-[#2C5545]/30 rounded-full px-2 py-0.5 font-medium">
                    <Check size={11} /> 確定済 · 編集不可
                  </span>
                ) : (
                  <span className="text-[#8A8A86]">下書き</span>
                )}
              </div>
            </div>
            <div className="flex gap-2 flex-wrap">
              {isNew && (
                <>
                  <Button variant="outline" size="sm" onClick={prefillFromMaster} className="border-[#2C5545] text-[#2C5545]">自動入力</Button>
                  <Button
                    size="sm"
                    data-testid="doc-create-draft-button"
                    onClick={createDraft}
                    className="bg-[#2C5545] text-white hover:bg-[#1F3D31]"
                  >
                    <FileText size={14} className="mr-1" />
                    下書き作成
                  </Button>
                </>
              )}
              {!isNew && !isFinalized && (
                <>
                  <Button variant="outline" size="sm" onClick={prefillFromMaster} className="border-[#2C5545] text-[#2C5545]">自動入力</Button>
                  <Button variant="outline" size="sm" data-testid="doc-save-button" onClick={save}>保存</Button>
                  <Button variant="outline" size="sm" data-testid="doc-finalize-button" onClick={finalize}><Check size={14} className="mr-1" />確定</Button>
                </>
              )}
              {isFinalized && (
                <Button variant="outline" size="sm" data-testid="doc-revert-button" onClick={revert} className="border-[#A74141] text-[#A74141] hover:bg-[#A74141]/5">
                  確定解除
                </Button>
              )}
              <Button variant="outline" size="sm" data-testid="doc-print-button" onClick={() => doc.doc_type === "instruction_request" ? printInstructionRequest(content, title, org) : printDocOnly(doc, content, schema, title, patient, org)}><Printer size={14} className="mr-1" />印刷</Button>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 flex-1 overflow-hidden">
            {/* EDIT PANEL */}
            <div className="no-print p-6 border-r border-[#E5E5E3] bg-[#FBFBF9] overflow-y-auto">
              <h4 className="font-bold mb-4 text-[#2C5545]">内容入力</h4>
              {isNew && (
                <div className="mb-4 p-3 rounded-lg border border-[#D4A373]/40 bg-[#FFF8E6] text-sm text-[#1C1C1A]">
                  <div className="flex items-center gap-2 font-bold text-[#8B6424]"><FileText size={14} /> まだ保存されていません</div>
                  <div className="text-xs text-[#4A4A48] mt-1">
                    内容を確認・編集して、上部の <span className="font-bold text-[#2C5545]">「下書き作成」</span> ボタンで初めてサーバーに保存されます。
                  </div>
                </div>
              )}
              {isFinalized && (
                <div className="mb-4 p-3 rounded-lg border border-[#2C5545]/30 bg-[#D9E2D5]/40 text-sm text-[#1C1C1A]">
                  <div className="flex items-center gap-2 font-bold text-[#2C5545]"><Check size={14} /> 確定済の書類です</div>
                  <div className="text-xs text-[#4A4A48] mt-1">
                    内容は編集できません。修正が必要な場合は、上部「確定解除」ボタンから一旦下書きに戻してください(管理者権限が必要)。
                  </div>
                </div>
              )}
              {!isFinalized && referrers.length > 0 && (
                <div className="mb-5 p-3 rounded-lg border border-[#4A6B8C]/30 bg-[#EEF3F8]" data-testid="doc-referrer-picker">
                  <Label className="text-xs text-[#4A6B8C] font-bold mb-1.5 block">医療機関マスタから自動入力</Label>
                  <select
                    data-testid="doc-referrer-select"
                    onChange={(e) => { if (e.target.value) { applyReferrer(e.target.value); e.target.value = ""; } }}
                    className="flex h-9 w-full rounded-md border border-[#4A6B8C]/40 px-3 text-sm bg-white"
                  >
                    <option value="">選択して紹介元・主治医・連絡先を自動入力…</option>
                    {referrers.map((r) => (
                      <option key={r.id} value={r.id}>
                        [{r.kind || "医療機関"}] {r.name}{r.person_in_charge ? ` / ${r.person_in_charge}` : ""}
                      </option>
                    ))}
                  </select>
                  <div className="text-[11px] text-[#4A6B8C]/80 mt-1.5">
                    紹介元・主治医氏名・連絡先・宛先・退院時病院名などを一括反映
                  </div>
                </div>
              )}
              {schema.sections.map((sec, si) => (
                <div key={si} className="mb-6">
                  <div className="flex items-center gap-2 mb-3 pb-1 border-b border-[#E5E5E3]">
                    <div className="w-1 h-4 bg-[#2C5545] rounded" />
                    <h5 className="font-medium text-sm text-[#1C1C1A]">{sec.title}</h5>
                  </div>
                  {sec.note && <div className="text-xs text-[#8A8A86] mb-2 italic">{sec.note}</div>}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {(sec.fields || []).map((f) => (
                      <div key={f.key} className={f.full ? "md:col-span-2" : ""}>
                        {f.type !== "check" && <Label className="text-xs">{f.label}</Label>}
                        {renderField(f)}
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>

            {/* PREVIEW */}
            <div className="bg-[#F3F3F1] p-4 md:p-6 overflow-y-auto">
              <div className="doc-a4" style={{ width: "100%", minHeight: "auto", padding: "14mm 12mm" }}>
                {doc.doc_type === "instruction_request" ? (
                  <InstructionRequestLetter content={content} org={org} title={title} />
                ) : (
                <>
                <div className="text-center mb-6">
                  <h1 className="text-2xl font-bold mb-1">{title}</h1>
                  <div className="text-xs font-numeric">
                    {content.issue_date || content.plan_date || content.contract_date || content.application_date || content.meeting_date || content.report_date || content.date || ""}
                  </div>
                </div>

                {patient && (
                  <section className="mb-4">
                    <h2 className="text-sm font-bold border-b border-black pb-1 mb-2">利用者情報</h2>
                    <table className="w-full text-xs">
                      <tbody>
                        <tr><td className="py-0.5 w-24 font-bold">氏名</td><td>{patient.name_kanji}({patient.name_kana || ""})</td></tr>
                        <tr><td className="py-0.5 font-bold">生年月日</td><td>{patient.birth_date}</td></tr>
                        <tr><td className="py-0.5 font-bold">住所</td><td>{patient.address}</td></tr>
                        <tr><td className="py-0.5 font-bold">電話</td><td>{patient.phone}</td></tr>
                        <tr><td className="py-0.5 font-bold">主治医</td><td>{patient.primary_doctor_name} / {patient.primary_medical_institution}</td></tr>
                      </tbody>
                    </table>
                  </section>
                )}

                {schema.sections.map((sec, si) => {
                  const nonEmpty = (sec.fields || []).filter((f) => {
                    if (f.type === "signature") return false;
                    const v = content[f.key];
                    return v !== undefined && v !== "" && v !== null && v !== false;
                  });
                  if (nonEmpty.length === 0) return null;
                  return (
                    <section key={si} className="mb-4">
                      <h2 className="text-sm font-bold border-b border-black pb-1 mb-2">{sec.title}</h2>
                      <table className="w-full text-xs">
                        <tbody>
                          {nonEmpty.map((f) => {
                            const v = content[f.key];
                            return (
                              <tr key={f.key} className="align-top">
                                <td className="py-0.5 pr-2 font-bold w-40">{f.label}</td>
                                <td className="py-0.5 whitespace-pre-wrap">
                                  {f.type === "check" ? (v ? "☑ 同意" : "") : String(v)}
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </section>
                  );
                })}

                <div className="mt-8 border-t border-black pt-4">
                  <div className="grid grid-cols-2 gap-6 text-xs">
                    <div>
                      <div className="font-bold mb-1">事業所</div>
                      <div>{org?.name || ""}</div>
                      <div>{org?.address || ""}</div>
                      <div>TEL: {org?.phone || ""}</div>
                      <div>管理者: {org?.manager_name || ""}</div>
                      <div>指定番号: {org?.care_insurance_number || ""}</div>
                      {content.provider_signature && (
                        <div className="mt-2">
                          <img src={content.provider_signature} alt="事業所署名" className="h-16 max-w-[220px] object-contain border-b border-black" />
                        </div>
                      )}
                    </div>
                    <div>
                      <div className="mb-6">
                        <div className="font-bold mb-1">利用者・ご家族</div>
                        <div>氏名: {content.client_name || "___________________"}　印</div>
                        {content.representative_name && <div>代理人: {content.representative_name}({content.representative_relation})</div>}
                        {content.client_signature && (
                          <div className="mt-2">
                            <img src={content.client_signature} alt="利用者署名" className="h-16 max-w-[220px] object-contain border-b border-black" />
                          </div>
                        )}
                        {content.signed_date && <div className="text-xs mt-1">署名日: {content.signed_date}</div>}
                      </div>
                      <div>
                        <div className="font-bold mb-1">事業所 代表</div>
                        <div>氏名: {content.provider_signer || "___________________"}　印</div>
                      </div>
                    </div>
                  </div>
                </div>
                </>
                )}
              </div>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
